import { supabase } from './supabaseClient'

// File storage service untuk mengelola file di Supabase Storage
export class FileStorageService {
  constructor() {
    this.bucketName = 'file'
    this.imageBucketName = 'gambar' // Bucket khusus untuk gambar
    this.videoBucketName = 'video' // Bucket khusus untuk video
  }

  // Helper untuk mendapatkan path berdasarkan user dan tipe file
  getUserFilePath(userId, fileType, fileName) {
    return `${userId}/${fileType}/${fileName}`
  }

  // Upload file ke storage
  async uploadFile(userId, fileType, file, fileName) {
    try {
      const filePath = this.getUserFilePath(userId, fileType, fileName)
      
      let { data, error } = await supabase.storage
        .from(this.bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (error) {
        console.error('❌ Upload error:', error)
        
        // Jika error karena unauthorized, coba refresh session
        if (error.statusCode === '403' || error.message?.includes('Unauthorized')) {
          console.log('🔄 Mencoba refresh session...')
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
          
          if (!refreshError && refreshData.session) {
            console.log('✅ Session berhasil di-refresh, mencoba upload ulang...')
            
            // Coba upload ulang dengan session yang baru
            const { data: retryData, error: retryError } = await supabase.storage
              .from(this.bucketName)
              .upload(filePath, file, {
                cacheControl: '3600',
                upsert: true
              })
            
            if (retryError) {
              console.error('❌ Upload ulang gagal:', retryError)
              throw retryError
            }
            
            // Update data dengan hasil retry
            data = retryData
            error = null
          } else {
            console.error('❌ Refresh session gagal:', refreshError)
            throw error
          }
        } else {
          throw error
        }
      }
      return { data, error: null }
    } catch (error) {
      console.error('Error uploading file:', error)
      return { data: null, error }
    }
  }

  // Upload text content sebagai file
  async uploadTextContent(userId, fileType, content, fileName) {
    try {
      const blob = new Blob([content], { type: 'text/plain' })
      return await this.uploadFile(userId, fileType, blob, fileName)
    } catch (error) {
      console.error('Error uploading text content:', error)
      return { data: null, error }
    }
  }

  // Mendapatkan daftar file user berdasarkan tipe
  async getUserFiles(userId, fileType = null) {
    try {
      const prefix = fileType ? `${userId}/${fileType}/` : `${userId}/`
      
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .list(prefix, {
          limit: 100,
          offset: 0
        })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error fetching user files:', error)
      return { data: null, error }
    }
  }

  // Mendapatkan semua file user dengan struktur folder
  async getAllUserFiles(userId) {
    try {
      const fileTypes = ['chat', 'artikel_agent', 'skripsi_agent', 'makalah_agent', 'gambar_agent', 'ppt_agent']
      const allFiles = {}

      for (const fileType of fileTypes) {
        const { data, error } = await this.getUserFiles(userId, fileType)
        if (!error && data) {
          allFiles[fileType] = data.filter(file => file.name !== '.emptyFolderPlaceholder')
        } else {
          allFiles[fileType] = []
        }
      }

      return { data: allFiles, error: null }
    } catch (error) {
      console.error('Error fetching all user files:', error)
      return { data: null, error }
    }
  }

  // Mendapatkan URL publik untuk file
  async getFileUrl(userId, fileType, fileName) {
    try {
      const filePath = this.getUserFilePath(userId, fileType, fileName)
      
      const { data } = supabase.storage
        .from(this.bucketName)
        .getPublicUrl(filePath)

      return { url: data.publicUrl, error: null }
    } catch (error) {
      console.error('Error getting file URL:', error)
      return { url: null, error }
    }
  }

  // Download file content
  async downloadFile(userId, fileType, fileName) {
    try {
      const filePath = this.getUserFilePath(userId, fileType, fileName)
      
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .download(filePath)

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error downloading file:', error)
      return { data: null, error }
    }
  }

  // Hapus file
  async deleteFile(userId, fileType, fileName) {
    try {
      const filePath = this.getUserFilePath(userId, fileType, fileName)
      
      const { data, error } = await supabase.storage
        .from(this.bucketName)
        .remove([filePath])

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      console.error('Error deleting file:', error)
      return { data: null, error }
    }
  }

  // Simpan hasil chat
  async saveChatResult(userId, query, response, timestamp = new Date()) {
    const fileName = `chat_${timestamp.getTime()}.json`
    const content = JSON.stringify({
      query,
      response,
      timestamp: timestamp.toISOString(),
      type: 'chat'
    }, null, 2)

    return await this.uploadTextContent(userId, 'chat', content, fileName)
  }

  // Simpan hasil artikel
  async saveArtikelResult(userId, title, content, timestamp = new Date()) {
    const fileName = `artikel_${timestamp.getTime()}.md`
    const markdownContent = `# ${title}\n\n${content}\n\n---\n*Generated on: ${timestamp.toISOString()}*`

    return await this.uploadTextContent(userId, 'artikel_agent', markdownContent, fileName)
  }

  // Simpan hasil skripsi
  async saveSkripsiResult(userId, title, content, timestamp = new Date()) {
    const fileName = `skripsi_${timestamp.getTime()}.md`
    const markdownContent = `# ${title}\n\n${content}\n\n---\n*Generated on: ${timestamp.toISOString()}*`

    return await this.uploadTextContent(userId, 'skripsi_agent', markdownContent, fileName)
  }

  // Simpan hasil makalah
  async saveMakalahResult(userId, title, content, timestamp = new Date()) {
    const fileName = `makalah_${timestamp.getTime()}.md`
    const markdownContent = `# ${title}\n\n${content}\n\n---\n*Generated on: ${timestamp.toISOString()}*`

    return await this.uploadTextContent(userId, 'makalah_agent', markdownContent, fileName)
  }

  // Simpan gambar
  async saveImageResult(userId, imageFile, description = '', timestamp = new Date()) {
    const fileExtension = imageFile.name.split('.').pop()
    const fileName = `image_${timestamp.getTime()}.${fileExtension}`
    
    // Upload gambar ke bucket khusus gambar dengan path yang benar
    const filePath = `${userId}/${fileName}` // Path khusus untuk bucket gambar
    
    const { data, error } = await supabase.storage
      .from(this.imageBucketName)
      .upload(filePath, imageFile, {
        cacheControl: '3600',
        upsert: true
      })

    if (error) {
      console.error('Error uploading image:', error)
      return { data: null, error }
    }
    
    // Dapatkan URL publik
    const { data: urlData } = supabase.storage
      .from(this.imageBucketName)
      .getPublicUrl(filePath)
    
    // Jika ada deskripsi, simpan juga metadata ke bucket gambar
    if (description && data) {
      const metadataFileName = `image_${timestamp.getTime()}_metadata.json`
      const metadata = JSON.stringify({
        originalName: imageFile.name,
        description,
        timestamp: timestamp.toISOString(),
        type: 'image',
        fileName,
        publicUrl: urlData.publicUrl
      }, null, 2)
      
      // Upload metadata langsung ke bucket gambar dengan path yang benar
      const metadataPath = `${userId}/${metadataFileName}`
      await supabase.storage
        .from(this.imageBucketName)
        .upload(metadataPath, new Blob([metadata], { type: 'application/json' }), {
          cacheControl: '3600',
          upsert: true
        })
    }
    
    const uploadResult = { 
      data: { 
        path: data.path, 
        publicUrl: urlData.publicUrl,
        fileName 
      }, 
      error: null 
    }
    
    return uploadResult
  }

  // Upload gambar dari URL eksternal ke Supabase storage
  async saveImageFromUrl(userId, imageUrl, prompt = '', metadata = {}, timestamp = new Date()) {
    try {
      console.log('🔄 Mengunduh gambar dari URL:', imageUrl)
      
      // Validasi session sebelum upload
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session || !session.user) {
        console.error('❌ Session tidak valid:', sessionError)
        throw new Error('User session tidak valid atau expired')
      }
      
      console.log('✅ Session valid, user ID:', session.user.id)
      console.log('✅ Session access token exists:', !!session.access_token)
      
      // Pastikan userId yang digunakan sama dengan session user ID
      const validUserId = session.user.id
      
      // Set auth header untuk memastikan RLS policy bekerja
      supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token
      })
      
      // Fetch gambar dari URL dengan headers yang proper
      const response = await fetch(imageUrl, {
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (compatible; ImageDownloader/1.0)'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`)
      }
      
      // Dapatkan content type dari response
      const contentType = response.headers.get('content-type') || 'image/png'
      const blob = await response.blob()
      
      // Validasi blob
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded image is empty or invalid')
      }
      
      if (blob.size > 50 * 1024 * 1024) { // 50MB limit
        throw new Error('Image file too large (max 50MB)')
      }
      
      // Tentukan ekstensi file berdasarkan content type
      let fileExtension = 'png'
      if (contentType.includes('jpeg') || contentType.includes('jpg')) {
        fileExtension = 'jpg'
      } else if (contentType.includes('webp')) {
        fileExtension = 'webp'
      } else if (contentType.includes('gif')) {
        fileExtension = 'gif'
      }
      
      const fileName = `generated_image_${timestamp.getTime()}.${fileExtension}`
      let filePath = `${validUserId}/${fileName}`
      
      console.log('🔄 Uploading to path:', filePath, 'with content type:', contentType, 'blob size:', blob.size)
      
      let { data, error } = await supabase.storage
        .from(this.imageBucketName)
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: contentType
        })

      if (error) {
        console.error('❌ Upload error:', error)
        console.error('❌ Error details:', {
          statusCode: error.statusCode,
          message: error.message,
          bucket: this.imageBucketName,
          filePath: filePath,
          userId: validUserId
        })
        
        // Jika error karena unauthorized, coba refresh session
        if (error.statusCode === '403' || error.message?.includes('Unauthorized') || error.message?.includes('row-level security')) {
          console.log('🔄 Mencoba refresh session dan re-authenticate...')
          
          // Refresh session
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
          
          if (!refreshError && refreshData.session) {
            console.log('✅ Session berhasil di-refresh')
            console.log('✅ New session user ID:', refreshData.session.user.id)
            
            // Set session yang baru
            await supabase.auth.setSession({
              access_token: refreshData.session.access_token,
              refresh_token: refreshData.session.refresh_token
            })
            
            // Tunggu sebentar untuk memastikan session ter-set
            await new Promise(resolve => setTimeout(resolve, 1000))
            
            // Coba upload ulang dengan session yang baru
            const { data: retryData, error: retryError } = await supabase.storage
              .from(this.imageBucketName)
              .upload(filePath, blob, {
                cacheControl: '3600',
                upsert: true,
                contentType: contentType
              })
            
            if (retryError) {
                console.error('❌ Upload ulang gagal:', retryError)
                console.error('❌ Retry error details:', {
                  statusCode: retryError.statusCode,
                  message: retryError.message,
                  bucket: this.imageBucketName,
                  filePath: filePath,
                  userId: refreshData.session.user.id
                })
                
                // Jika masih gagal, coba dengan path alternatif
                console.log('🔄 Mencoba dengan path alternatif...')
                const alternativeFilePath = `public/${fileName}`
                
                const { data: altData, error: altError } = await supabase.storage
                  .from(this.imageBucketName)
                  .upload(alternativeFilePath, blob, {
                    cacheControl: '3600',
                    upsert: true,
                    contentType: contentType
                  })
                
                if (altError) {
                  console.error('❌ Upload dengan path alternatif juga gagal:', altError)
                  throw retryError // Throw original error
                } else {
                  console.log('✅ Upload berhasil dengan path alternatif')
                  data = altData
                  error = null
                  // Update filePath untuk URL generation dan metadata
                  filePath = alternativeFilePath
                }
              } else {
                console.log('✅ Upload berhasil setelah refresh session')
                // Update data dengan hasil retry
                data = retryData
                error = null // Reset error setelah berhasil
              }
          } else {
            console.error('❌ Refresh session gagal:', refreshError)
            throw error
          }
        } else {
          throw error
        }
      }
      
      // Dapatkan URL publik dari Supabase
      const { data: urlData } = supabase.storage
        .from(this.imageBucketName)
        .getPublicUrl(filePath)
      
      console.log('✅ Gambar berhasil disimpan ke Supabase storage:', urlData.publicUrl)
      
      // Simpan metadata ke bucket gambar dengan path yang benar
      const metadataFileName = `generated_image_${timestamp.getTime()}_metadata.json`
      const metadataContent = JSON.stringify({
        originalUrl: imageUrl,
        prompt,
        timestamp: timestamp.toISOString(),
        type: 'generated_image',
        fileName,
        supabaseUrl: urlData.publicUrl,
        contentType,
        fileSize: blob.size,
        ...metadata
      }, null, 2)
      
      // Upload metadata langsung ke bucket gambar dengan path yang benar
      // Gunakan folder yang sama dengan file gambar
      const metadataPath = filePath.includes('public/') 
        ? `public/${metadataFileName}` 
        : `${validUserId}/${metadataFileName}`
        
      console.log('🔄 Uploading metadata to path:', metadataPath)
      
      try {
        await supabase.storage
          .from(this.imageBucketName)
          .upload(metadataPath, new Blob([metadataContent], { type: 'application/json' }), {
            cacheControl: '3600',
            upsert: true
          })
        console.log('✅ Metadata berhasil disimpan')
      } catch (metadataError) {
        console.warn('⚠️ Gagal menyimpan metadata, tapi gambar sudah tersimpan:', metadataError)
        // Tidak throw error karena gambar sudah berhasil disimpan
      }
      
      return { 
        data: { 
          path: data.path, 
          publicUrl: urlData.publicUrl,
          fileName 
        }, 
        error: null 
      }
    } catch (error) {
      console.error('❌ Error menyimpan gambar dari URL:', error)
      return { data: null, error }
    }
  }

  // Upload video dari URL eksternal ke Supabase storage
  async saveVideoFromUrl(userId, videoUrl, prompt = '', metadata = {}, timestamp = new Date()) {
    try {
      console.log('🔄 Mengunduh video dari URL:', videoUrl)
      
      // Validasi URL sebelum fetch
      if (!videoUrl || typeof videoUrl !== 'string') {
        throw new Error('URL video tidak valid')
      }
      
      // Cek apakah URL adalah URL yang valid
      try {
        new URL(videoUrl)
      } catch (urlError) {
        console.error('❌ URL tidak valid:', videoUrl)
        throw new Error('Format URL video tidak valid')
      }
      
      // Validasi session sebelum upload dengan retry
      let session
      let sessionError
      
      // Try to get session, refresh if needed
      const { data: sessionData, error: getSessionError } = await supabase.auth.getSession()
      
      if (getSessionError || !sessionData.session || !sessionData.session.user) {
        console.log('🔄 Session tidak valid, mencoba refresh...')
        
        // Try to refresh session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
        
        if (refreshError || !refreshData.session) {
          console.error('❌ Gagal refresh session:', refreshError)
          throw new Error('User session tidak valid atau expired. Silakan login ulang.')
        }
        
        session = refreshData.session
        console.log('✅ Session berhasil di-refresh')
      } else {
        session = sessionData.session
      }
      
      console.log('✅ Session valid, user ID:', session.user.id)
      const validUserId = session.user.id
      
      // Ensure session is properly set
      await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token
      })
      
      // Wait a bit to ensure session is set
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Fetch video dari URL dengan retry mechanism
      let response
      let lastError
      const maxRetries = 3
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`🔄 Fetching video from URL (attempt ${attempt}/${maxRetries}):`, videoUrl)
          
          response = await fetch(videoUrl, {
            method: 'GET',
            headers: {
              'User-Agent': 'Mozilla/5.0 (compatible; VideoDownloader/1.0)',
              'Accept': 'video/*,*/*;q=0.9'
            },
            timeout: 30000 // 30 second timeout
          })
          
          console.log('📊 Fetch response status:', response.status, response.statusText)
          console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()))
          
          if (response.ok) {
            break // Success, exit retry loop
          }
          
          // Log error but continue to retry logic
          console.warn(`⚠️ Attempt ${attempt} failed:`, {
            status: response.status,
            statusText: response.statusText,
            url: videoUrl
          })
          
          // Don't retry for certain status codes
          if (response.status === 404 || response.status === 403) {
            break
          }
          
          lastError = new Error(`HTTP ${response.status}: ${response.statusText}`)
          
        } catch (fetchError) {
          console.warn(`⚠️ Fetch attempt ${attempt} failed:`, fetchError.message)
          lastError = fetchError
        }
        
        // Wait before retry (except on last attempt)
        if (attempt < maxRetries) {
          const delay = attempt * 2000 // 2s, 4s delay
          console.log(`⏳ Waiting ${delay}ms before retry...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
      
      if (!response || !response.ok) {
        console.error('❌ All fetch attempts failed:', {
          lastError: lastError?.message,
          status: response?.status,
          statusText: response?.statusText,
          url: videoUrl
        })
        
        if (response?.status === 400) {
          throw new Error(`Video URL tidak valid atau sudah expired. Status: ${response.status}`)
        } else if (response?.status === 403) {
          throw new Error(`Akses ke video ditolak. Status: ${response.status}`)
        } else if (response?.status === 404) {
          throw new Error(`Video tidak ditemukan. Status: ${response.status}`)
        } else {
          throw new Error(`Gagal mengunduh video setelah ${maxRetries} percobaan: ${lastError?.message || 'Unknown error'}`)
        }
      }
      
      // Dapatkan content type dari response
      const contentType = response.headers.get('content-type') || 'video/mp4'
      let blob = await response.blob()
      
      // Validasi blob
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded video is empty or invalid')
      }
      
      if (blob.size > 100 * 1024 * 1024) { // 100MB limit for videos
        throw new Error('Video file too large (max 100MB)')
      }
      
      // Tentukan ekstensi file berdasarkan content type
      let fileExtension = 'mp4'
      if (contentType.includes('webm')) {
        fileExtension = 'webm'
      } else if (contentType.includes('avi')) {
        fileExtension = 'avi'
      } else if (contentType.includes('mov')) {
        fileExtension = 'mov'
      }
      
      const fileName = `generated_video_${timestamp.getTime()}.${fileExtension}`
      let filePath = `${validUserId}/${fileName}`
      
      console.log('🔄 Uploading video to path:', filePath, 'with content type:', contentType, 'blob size:', blob.size)
      
      // Validasi bucket sebelum upload
      try {
        const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets()
        if (bucketsError) {
          console.error('❌ Error checking buckets:', bucketsError)
        } else {
          const videoBucket = buckets.find(bucket => bucket.name === this.videoBucketName)
          if (!videoBucket) {
            console.error('❌ Video bucket not found. Available buckets:', buckets.map(b => b.name))
            throw new Error(`Bucket '${this.videoBucketName}' tidak ditemukan di Supabase storage`)
          }
          console.log('✅ Video bucket found:', videoBucket)
        }
      } catch (bucketError) {
        console.warn('⚠️ Could not validate bucket, proceeding with upload:', bucketError.message)
      }
      
      // Enhanced blob validation before upload
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Invalid blob data for upload')
      }
      
      // Additional blob validations
      if (blob.size === 0) {
        throw new Error('Blob is empty - cannot upload empty video file')
      }
      
      if (blob.size < 1024) { // Less than 1KB is suspicious for a video
        console.warn('⚠️ Video file is very small:', blob.size, 'bytes')
      }
      
      // Validate content type
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/avi', 'video/mov', 'video/quicktime', 'application/octet-stream']
      if (contentType && !validVideoTypes.some(type => contentType.includes(type.split('/')[1]) || contentType === type)) {
        console.warn('⚠️ Unusual content type for video:', contentType)
      }
      
      // Ensure blob type is set correctly
      if (!blob.type && contentType) {
        // Create a new blob with correct type if needed
        blob = new Blob([blob], { type: contentType })
        console.log('🔧 Fixed blob type to:', contentType)
      }
      
      console.log('📊 Upload details:', {
        bucketName: this.videoBucketName,
        filePath,
        contentType,
        blobSize: blob.size,
        blobType: blob.type,
        fileName,
        validUserId
      })
      
      // Prepare upload options with better error handling
      const uploadOptions = {
        cacheControl: '3600',
        upsert: true,
        contentType: contentType || 'video/mp4'
      }
      
      console.log('🚀 Starting upload with options:', uploadOptions)
      
      // Try upload with comprehensive error handling
      let { data, error } = await supabase.storage
        .from(this.videoBucketName)
        .upload(filePath, blob, uploadOptions)

      if (error) {
        console.error('❌ Upload error details:', {
          message: error.message,
          statusCode: error.statusCode,
          error: error,
          filePath,
          bucketName: this.videoBucketName,
          blobSize: blob.size,
          contentType,
          uploadOptions
        })
        
        // Handle specific error cases with more detailed analysis
        if (error.statusCode === '400' || error.message?.includes('400') || error.message?.includes('Bad Request')) {
          console.error('❌ Bad Request (400) error detected')
          
          // Check for specific 400 error causes
          if (error.message?.includes('Invalid file') || error.message?.includes('invalid file')) {
            throw new Error('File video tidak valid atau format tidak didukung')
          } else if (error.message?.includes('too large') || error.message?.includes('file size')) {
            throw new Error('Ukuran file terlalu besar (maksimal 100MB)')
          } else if (error.message?.includes('Invalid bucket') || error.message?.includes('bucket')) {
            throw new Error('Bucket video tidak valid atau tidak ditemukan')
          } else if (error.message?.includes('mime') || error.message?.includes('content-type')) {
            throw new Error('Tipe file tidak didukung. Gunakan format video yang valid (MP4, WebM, AVI, MOV)')
          } else if (error.message?.includes('path') || error.message?.includes('filename')) {
            throw new Error('Nama file atau path tidak valid')
          } else if (error.message?.includes('policy') || error.message?.includes('permission')) {
            throw new Error('Tidak memiliki izin untuk upload ke bucket video')
          } else {
            throw new Error(`Upload gagal (400): ${error.message}. Periksa format file dan koneksi internet.`)
          }
        }
        
        // Handle 403/unauthorized errors with session refresh
        if (error.statusCode === '403' || error.message?.includes('Unauthorized') || error.message?.includes('row-level security') || error.message?.includes('permission')) {
          console.log('🔄 Mencoba refresh session dan re-authenticate...')
          
          try {
            // Refresh session
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
            
            if (refreshError || !refreshData.session) {
              console.error('❌ Refresh session gagal:', refreshError)
              throw new Error('Session expired. Silakan login ulang.')
            }
            
            console.log('✅ Session berhasil di-refresh')
            
            // Set session yang baru
            await supabase.auth.setSession({
              access_token: refreshData.session.access_token,
              refresh_token: refreshData.session.refresh_token
            })
            
            // Tunggu untuk memastikan session ter-set
            await new Promise(resolve => setTimeout(resolve, 1500))
            
            // Validate blob again before retry
            if (!blob || blob.size === 0) {
              throw new Error('Blob data tidak valid untuk retry upload')
            }
            
            console.log('🔄 Mencoba upload ulang dengan session baru...')
            
            // Coba upload ulang dengan session yang baru
            const { data: retryData, error: retryError } = await supabase.storage
              .from(this.videoBucketName)
              .upload(filePath, blob, uploadOptions)
            
            if (retryError) {
              console.error('❌ Upload ulang gagal:', retryError)
              if (retryError.statusCode === '400') {
                throw new Error(`Upload gagal setelah refresh session: ${retryError.message}. Kemungkinan masalah dengan file atau konfigurasi bucket.`)
              } else {
                throw new Error(`Upload gagal setelah refresh session: ${retryError.message}`)
              }
            } else {
              console.log('✅ Upload berhasil setelah refresh session')
              data = retryData
              error = null
            }
          } catch (refreshRetryError) {
            console.error('❌ Error dalam proses refresh dan retry:', refreshRetryError)
            throw refreshRetryError
          }
        } else {
          // For other errors, throw immediately
          throw error
        }
      }
      
      // Dapatkan URL publik dari Supabase
      const { data: urlData } = supabase.storage
        .from(this.videoBucketName)
        .getPublicUrl(filePath)
      
      console.log('✅ Video berhasil disimpan ke Supabase storage:', urlData.publicUrl)
      
      // Simpan metadata
      const metadataFileName = `generated_video_${timestamp.getTime()}_metadata.json`
      const metadataContent = JSON.stringify({
        originalUrl: videoUrl,
        prompt,
        timestamp: timestamp.toISOString(),
        type: 'generated_video',
        fileName,
        supabaseUrl: urlData.publicUrl,
        contentType,
        fileSize: blob.size,
        ...metadata
      }, null, 2)
      
      const metadataPath = `${validUserId}/${metadataFileName}`
      
      try {
        await supabase.storage
          .from(this.videoBucketName)
          .upload(metadataPath, new Blob([metadataContent], { type: 'application/json' }), {
            cacheControl: '3600',
            upsert: true
          })
        console.log('✅ Video metadata berhasil disimpan')
      } catch (metadataError) {
        console.warn('⚠️ Gagal menyimpan metadata, tapi video sudah tersimpan:', metadataError)
      }
      
      return { 
        data: { 
          path: data.path, 
          publicUrl: urlData.publicUrl,
          fileName 
        }, 
        error: null 
      }
    } catch (error) {
      console.error('❌ Error menyimpan video dari URL:', error)
      return { data: null, error }
    }
  }
}

// Export instance
export const fileStorageService = new FileStorageService()
export default fileStorageService